<script setup lang="ts">
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useUploadStore from '@browser/stores/upload'
import useAppStore from '@browser/stores/app'
import useThemeVars from '@lib/composables/useThemeVars'
import { useI18n } from 'vue-i18n'
import { onMounted, ref, onBeforeUnmount } from 'vue'
import type { DeviceFile } from '@lib/types/files'

const props = defineProps<{
  area?: HTMLElement
}>()

const { t } = useI18n()
const themeVars = useThemeVars()

const {
  state: { uploadSelectedFiles },
} = useUploadStore()

const { setCurrentState } = useAppStore()

const dropzoneRef = ref<HTMLElement>()
const targetEl = ref<EventTarget | null>(null)

const dragEventContainsFiles = (event: Event) => {
  const e = event as DragEvent
  const dt = e.dataTransfer

  if (!dt?.types?.length) return false

  return dt.types.some((type) => type === 'Files')
}

const onDragEnter = (event: Event) => {
  const e = event as DragEvent
  if (!dropzoneRef.value || !dragEventContainsFiles(e)) return
  targetEl.value = e.target // cache the last target here
  dropzoneRef.value.style.visibility = ''
  dropzoneRef.value.style.opacity = '1'
}

const onDragLeave = (event: Event) => {
  const e = event as DragEvent
  e.preventDefault()
  // `e.target !== document` is a workaround for Firefox 57
  if (!dropzoneRef.value || (e.target !== targetEl.value && e.target !== document)) return

  dropzoneRef.value.style.visibility = 'hidden'
  dropzoneRef.value.style.opacity = '0'
}

const onDragOver = (event: Event) => {
  event.preventDefault()
}

const onDrop = (event: Event) => {
  const e = event as DragEvent
  if (!dropzoneRef.value || !dragEventContainsFiles(e)) return
  e.preventDefault()
  dropzoneRef.value.style.visibility = 'hidden'
  dropzoneRef.value.style.opacity = '0'

  const droppedFiles = Array.from(e.dataTransfer?.files ?? [])

  const uniqueFiles = droppedFiles.filter(
    (file) =>
      !uploadSelectedFiles.value.find(
        (existingFile) =>
          existingFile.name === file.name &&
          existingFile.size === file.size &&
          ('lastModified' in existingFile ? existingFile.lastModified === file.lastModified : true),
      ),
  )

  uploadSelectedFiles.value = [...uploadSelectedFiles.value, ...uniqueFiles] as DeviceFile[] | File[]

  setCurrentState('upload-files')
}

onMounted(() => {
  const area = props.area ?? window

  area.addEventListener('dragenter', onDragEnter as EventListener)
  area.addEventListener('dragleave', onDragLeave as EventListener)
  area.addEventListener('dragover', onDragOver as EventListener)
  area.addEventListener('drop', onDrop as EventListener)
})

onBeforeUnmount(() => {
  const area = props.area ?? window

  area.removeEventListener('dragenter', onDragEnter as EventListener)
  area.removeEventListener('dragleave', onDragLeave as EventListener)
  area.removeEventListener('dragover', onDragOver as EventListener)
  area.removeEventListener('drop', onDrop as EventListener)
})
</script>

<template>
  <div ref="dropzoneRef" class="upload-files-dropzone" :style="{ visibility: 'hidden', opacity: 0 }">
    <div class="pa-8 mb-5 w-[500px] upload-files-dropzone__banner">
      <div class="flex justify-center items-center">
        <div class="mr-4">
          <CIcon :color="themeVars.base" icon="upload" size="large" />
        </div>
        <div class="font-bold upload-files-dropzone__banner-msg">
          {{ t('canvasUI.browserApp.uploadFilesDropzone.defaultDropMessage') }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.upload-files-dropzone {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10000; // always on top (adjust accordingly)
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  transition:
    visibility 175ms,
    opacity 175ms;

  &__banner {
    background: v-bind('themeVars.primary');
    border-radius: 16px;
    position: fixed;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    max-width: 602px;

    &-msg {
      font-size: 16px;
      color: v-bind('themeVars.base');
    }
  }
}
</style>
